import { pipe } from 'fp-ts/function';
import { connect } from 'react-redux';
import { reduxForm, SubmitHandler } from 'redux-form';
import { createStructuredSelector } from 'reselect';
import { ButtonProps, Stack, StructuredDate } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { AppClockServiceProps, LocalizedText, withAppClockService } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ApplicationState } from '@mews-commander/types';
import { DateIntervalFields, dateString, presence } from '@mews-commander/legacy-form';
import { getTodayStructuredDate } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { FORM_NAME } from '../constants';
import { getLocalizedFilterValues } from '../selectors';
import { FilterDateValues } from '../types';

interface Props {
    onFiltersSubmit: (values: FilterDateValues) => void;
    closeDialog: () => void;
    active: boolean;
}

interface PayoutsFilterDialogProps {
    maxDate: StructuredDate;
    form: string;
    handleSubmit: SubmitHandler;
}

const commonDateConstraints = {
    dateString,
    presence,
};

const dateConstraints = {
    startDate: commonDateConstraints,
    endDate: commonDateConstraints,
};

const startProps = {
    label: <LocalizedText textKey={TextKey.Start} />,
    required: true,
};

const endProps = {
    label: <LocalizedText textKey={TextKey.End} />,
    required: true,
};

const PayoutsFilterSimpleDetailForm = ({ form, maxDate }: { form: string; maxDate: StructuredDate }) => (
    <Stack spacing="100">
        <DateIntervalFields
            constraints={dateConstraints}
            fieldNames={['startDate', 'endDate']}
            startProps={startProps}
            endProps={endProps}
            // @ts-expect-error Property 'maxDate' does not exist on type 'IntrinsicAttributes & IntrinsicClassAttributes<DateIntervalFields
            maxDate={maxDate}
            form={form}
        />
    </Stack>
);

const PayoutsFilterDialog = ({
    active,
    maxDate,
    form,
    closeDialog,
    handleSubmit,
}: PayoutsFilterDialogProps & Props) => {
    const actions: ButtonProps[] = [
        {
            onClick: handleSubmit,
            children: <LocalizedText textKey={TextKey.PayoutApplyFilters} />,
            variant: 'primary',
            type: 'submit',
        },
    ];

    return (
        <FloatingPanel
            modal={false}
            active={active}
            actions={actions}
            title={<LocalizedText textKey={TextKey.PayoutFilterPayouts} />}
            onClose={closeDialog}
        >
            <PayoutsFilterSimpleDetailForm maxDate={maxDate} form={form} />
        </FloatingPanel>
    );
};

interface OwnProps {
    onFiltersSubmit: (values: FilterDateValues) => void;
    closeDialog: () => void;
    active: boolean;
}

interface StateProps {
    maxDate: StructuredDate;
    initialValues: FilterDateValues;
}

interface DispatchProps {
    onSubmit: (values: FilterDateValues) => void;
}

export const PayoutReportSidebar = pipe(
    PayoutsFilterDialog,
    reduxForm({
        form: FORM_NAME,
        enableReinitialize: true,
    }),
    connect<StateProps, DispatchProps, OwnProps & AppClockServiceProps, ApplicationState>(
        createStructuredSelector({
            maxDate: getTodayStructuredDate,
            initialValues: getLocalizedFilterValues,
        }),
        (_, { onFiltersSubmit }) => ({
            onSubmit: (values: FilterDateValues) => onFiltersSubmit(values),
        })
    ),
    withAppClockService
);
